import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { XIcon } from '../assets/X';

import { Drawer, DrawerContent, DrawerTitle } from './ui/drawer';
import { userInfo } from '../store/selectors/user';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form } from './ui/form';
import InputField from './form/InputFiled';

import { setUser } from '../store/slices/userSlice';
import { useUpdateUser } from '../modules/profile/services/update';
import { LoadingButton } from './ui/loadingButton';
import { NAME_MAX_LENGTH, nameValueSchema, sanitizeName } from '../utils/name';

type QrCardProps = {
  open: boolean;
  setOpen: (openQr: boolean) => void;
};

// Правила для имени общие с шагом регистрации — держим их в одном месте.
const nameSchema = z.object({
  name: nameValueSchema,
});
type NameFormSchema = z.infer<typeof nameSchema>;

export const NameDrawer = ({ open, setOpen }: QrCardProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  // Меняется одно поле — и тост должен говорить про имя, а не про «данные».
  const { updateUser, isPending } = useUpdateUser(t('nameUpdated'));
  const user = useSelector(userInfo);

  const form = useForm<NameFormSchema>({
    resolver: zodResolver(nameSchema),
    defaultValues: {
      name: user.name ?? '',
    },
  });

  // Шторка живёт в дереве постоянно, а профиль подтягивается асинхронно —
  // defaultValues к моменту загрузки уже зафиксированы. Поэтому подставляем
  // актуальное имя на каждом открытии (и заодно сбрасываем правки, если
  // прошлый заход закрыли без сохранения).
  useEffect(() => {
    if (open) {
      form.reset({ name: user.name ?? '' });
    }
  }, [open, user.name, form]);

  const name = form.watch('name') ?? '';
  const currentName = (user.name ?? '').trim();
  const isUnchanged = name.trim() === currentName;

  const handleSubmit = async (data: NameFormSchema) => {
    // Имя не трогали — на сервер не ходим, иначе пользователь получает
    // «данные обновлены» на пустом месте.
    if (data.name.trim() === currentName) {
      setOpen(false);
      return;
    }

    try {
      const res = await updateUser({
        name: data.name.trim(),
      });

      if (res.data.success) {
        dispatch(
          setUser({
            ...user,
            name: data.name.trim(),
          })
        );
        setOpen(false);
      }
    } catch {
      // Ошибку показал useUpdateUser — шторку оставляем открытой,
      // чтобы введённое имя не пропало.
    }
  };

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerContent className="bg-fill-background rounded-t-[24px] px-4">
        <div className="flex flex-row justify-between items-center pb-4">
          <DrawerTitle className="text-display-md">
            {t('nameLabel')}
          </DrawerTitle>
          <div
            className="flex items-center justify-center h-6 w-6 rounded-full bg-button-secondary"
            onClick={() => setOpen(false)}
          >
            <XIcon className="text-text-icon-secondary" />
          </div>
        </div>

        <Form {...form}>
          <form
            className="flex flex-col space-y-5 pb-8 w-full"
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <InputField
              name="name"
              maxLength={NAME_MAX_LENGTH}
              autoComplete="name"
              // onChange перекрывает обработчик react-hook-form (InputField
              // раскладывает inputProps после field), поэтому значение кладём
              // в форму сами — уже очищенным.
              onChange={e =>
                form.setValue('name', sanitizeName(e.target.value), {
                  shouldDirty: true,
                })
              }
              className="w-full h-16 border-none text-[22px] font-semibold rounded-[16px] leading-7 tracking-[0px] caret-button-primary px-4 py-[18px]"
            />
            <LoadingButton
              loading={isPending}
              disabled={!name.trim() || isUnchanged}
              type="submit"
              className="w-full bg-button-primary h-12 text-[16px] font-semibold font-inter leading-5 tracking-[0px] text-text-white rounded-xl disabled:opacity-60"
            >
              {t('doneButton')}
            </LoadingButton>
          </form>
        </Form>
      </DrawerContent>
    </Drawer>
  );
};
